import { getCurrentLocaleCode, trans } from "@mongez/localization";
import { useLocation } from "@remix-run/react";
import { cn } from "core/shared";
import { URLS } from "framework/config/urls";
import { Fragment } from "react";
import { BaseLink, Button } from "~/design-system/components";
import { buildTree } from "~/modules/shared/utils/build-tree";
import { MenuCategory, MenuItem } from "~/shared/types";

export type DropdownMenuProps = {
  documents?: MenuItem[];
};

export function DropdownMenu({ documents }: DropdownMenuProps) {
  if (!documents || !documents.length) return null;

  const flatterDocuments = buildTree(documents);
  const { pathname, search } = useLocation();
  const currentPath = pathname + search;
  const localeCode = getCurrentLocaleCode();

  const isActive = (to?: string) => {
    if (!to) return false;
    const localizedURL = `/${localeCode}${to}`;
    return (
      currentPath === localizedURL ||
      (to !== "/" && currentPath.startsWith(`${localizedURL}/`))
    );
  };

  return (
    <div className="hidden w-full justify-center lg:flex">
      <ul className="font-jost flex flex-wrap items-center justify-center gap-4 text-[#0A0A0A]">
        {flatterDocuments.map((document) => (
          <Fragment key={document.id}>
            {document.type === "link" && (
              <BaseLink
                to={document.link || "#"}
                className={cn(
                  "px-2 pb-2 text-lg transition-all hover:text-[#FF506C]",
                  isActive(document.link) && "text-primary-default font-medium",
                )}
              >
                {trans(document.name)}
              </BaseLink>
            )}
            {document.category && (
              <BaseLink
                to={URLS.shop.viewCategory(document.category)}
                className={cn(
                  "px-2 text-lg transition-all hover:text-[#FF506C]",
                  isActive(URLS.shop.viewCategory(document.category)) &&
                    "text-primary-default font-medium",
                )}
              >
                {trans(document.name)}
              </BaseLink>
            )}

            {document.children && (
              <li className="group relative px-2">
                <Button
                  className={cn(
                    "w-fit px-0! text-lg! transition-all hover:text-[#FF506C]",
                  )}
                >
                  {trans(document.name)}
                </Button>
                {/* Menu */}
                <div
                  className={cn(
                    "invisible absolute -start-25 top-8 z-50 min-w-3xl translate-y-4 opacity-0 transition-all duration-300 ease-out lg:group-hover:visible lg:group-hover:translate-y-0 lg:group-hover:opacity-100 2xl:start-[50%] 2xl:min-w-6xl 2xl:translate-x-[-30%]",
                  )}
                >
                  <div className="h-fit rounded-xl bg-white p-5 shadow-xl xl:rounded-3xl">
                    <ul
                      className={cn(
                        "grid gap-6",
                        document.children.length === 1 && "grid-cols-1",
                        document.children.length === 2 && "grid-cols-2",
                        document.children.length >= 3 &&
                          "3xl:grid-cols-3 grid-cols-2",
                      )}
                    >
                      {document.children?.map((item) => (
                        <Fragment key={item.id}>
                          {item.children && (
                            <div>
                              <h6 className="mb-3 w-full border-b border-[#E4E4E4] pb-3 text-xl font-semibold text-black uppercase">
                                {item.name}
                              </h6>
                              <div className="flex flex-col gap-2">
                                {item.children?.map((child) => (
                                  <div key={child.id}>
                                    {child.category && (
                                      <BaseLink
                                        to={URLS.shop.viewCategory(
                                          child.category,
                                        )}
                                        className={cn(
                                          "text-lg transition-all hover:text-[#FF506C]",
                                          isActive(
                                            URLS.shop.viewCategory(
                                              child.category,
                                            ),
                                          ) &&
                                            "text-primary-default font-medium",
                                        )}
                                      >
                                        {trans(child.name)}
                                      </BaseLink>
                                    )}
                                    {child.link &&
                                      child.type !== "category" && (
                                        <BaseLink
                                          to={child.link}
                                          className={cn(
                                            "text-lg transition-all hover:text-[#FF506C]",
                                            isActive(child.link) &&
                                              "text-primary-default",
                                          )}
                                        >
                                          {trans(child.name)}
                                        </BaseLink>
                                      )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {item.link && item.type !== "category" && (
                            <BaseLink
                              to={item.link}
                              className={cn(
                                "text-lg transition-all hover:text-[#FF506C]",
                                isActive(item.link) && "text-primary-default",
                              )}
                            >
                              {trans(item.name)}
                            </BaseLink>
                          )}
                          {item.type === "category" && (
                            <BaseLink
                              to={URLS.shop.viewCategory(
                                item.category as MenuCategory,
                              )}
                              className={cn(
                                "text-lg transition-all hover:text-[#FF506C]",
                                isActive(
                                  URLS.shop.viewCategory(
                                    item.category as MenuCategory,
                                  ),
                                ) && "text-primary-default",
                              )}
                            >
                              {trans(item.name)}
                            </BaseLink>
                          )}
                        </Fragment>
                      ))}
                    </ul>
                  </div>
                </div>
              </li>
            )}
          </Fragment>
        ))}
      </ul>
    </div>
  );
}
